<script setup lang="ts">
import { AppPermissionsEnum } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import ExternalLink from '@/components/ExternalLink/ExternalLink.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { useSystemStore } from '@/stores/systemStore.ts';
import { getUrlComposition } from '@/utils/url.ts';
import { useAppUpdate } from '@/utils/useAppUpdate.ts';
import { useLoading } from '@/utils/useLoading.ts';
import AvailableUpdateInfo from '@/views/UpdateView/components/AvailableUpdateInfo.vue';
import UpdateWizard, { UpdateType } from '@/views/UpdateView/components/UpdateWizard.vue';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const permissionsStore = usePermissionsStore();

const { t } = useTranslation();

const systemStore = useSystemStore();
const router = useRouter();
const route = useRoute();
const {
  available,
  checkForNewUpdates,
  syncPtwEdgeUpdateStatus,
  ptwEdgeUpdateStatus,
  installedVersion,
  checkForUpdatesErrorNotification,
} = useAppUpdate(t, () => undefined);

const showStatus = ref(false);
const checkedForUpdates = ref(false);
const selectedUpdateType = ref<UpdateType>('update');

const shouldSyncPtwEdgeUpdateStatus = computed(() => {
  const { searchParams } = getUrlComposition(route.fullPath);
  if (searchParams?.has('sync')) {
    return searchParams.get('sync');
  } else {
    return false;
  }
});
const updateTypeFromUri = computed(() => {
  const { searchParams } = getUrlComposition(route.fullPath);
  if (searchParams?.has('updateType')) {
    return searchParams.get('updateType') as UpdateType;
  } else {
    return 'update';
  }
});

// sync the update status with the reflected model
const isUpdateAvailable = computed(() => !!available.value.update || !!available.value.upgrade);

function resetQueryParameters() {
  router.replace(`${route.path}${route.hash}`);
}
const { action: loadUpdateInfo, loading } = useLoading(async () => {
  checkedForUpdates.value = false;
  resetQueryParameters();
  await checkForNewUpdates();
  checkedForUpdates.value = true;
}, true);

watchEffect(async () => {
  if (route.hash.includes('update') && shouldSyncPtwEdgeUpdateStatus.value) {
    selectedUpdateType.value = updateTypeFromUri.value;
    await syncPtwEdgeUpdateStatus(selectedUpdateType.value);
    if (ptwEdgeUpdateStatus) {
      showStatus.value = true;
      openUpdateWizard(updateTypeFromUri.value);
    }
  }
});

onMounted(async () => {
  await loadUpdateInfo();
});

// Update wizard
const [isUpdateWizardOpen, toggleUpdateWizard] = useToggle(false);

function openUpdateWizard(type: UpdateType, resetQueryParams = false) {
  if (resetQueryParams) {
    resetQueryParameters();
  }
  selectedUpdateType.value = type;

  if (available.value) {
    toggleUpdateWizard(true);
  }
}
function handleWizardState(wizardOpen: boolean) {
  if (!wizardOpen) {
    showStatus.value = false;
  }
}

const upToDateNotification = computed<NotificationMessage>(() => ({
  type: 'success',
  title: t('YouAreAlreadyRunningTheLatestVersion', {
    installedVersion: installedVersion.value,
  }),
  message: '',
  closable: false,
}));
</script>

<template>
  <BasePage>
    <template #title>
      <BasePageTitle :title="$t('Update')" />
    </template>

    <template #actions>
      <PtwCancelButton :to="{ name: routenames.settings }" />
      <Button
        class="ml-2"
        :label="$t('CheckForNewUpdates')"
        variant="primary"
        :loading="loading"
        :disabled="
          !permissionsStore.hasPermissions([AppPermissionsEnum.GlobalSystemSettingsUpdate])
        "
        @click="loadUpdateInfo()"
      />
    </template>

    <v-sheet class="pa-4">
      <div class="text-body-1">
        <h4 class="mb-2">
          {{
            $t('CurrentPtw100SoftwareVersion', {
              version: systemStore.version,
            })
          }}
        </h4>

        <v-row>
          <v-col>
            <v-label> {{ $t('ReleasedOn') }} </v-label>
            <div>
              <DateTimeFormatter :value="systemStore.releaseDate" />
            </div>
          </v-col>

          <v-col>
            <v-label> {{ $t('InstalledOn') }} </v-label>
            <div>
              <DateTimeFormatter :value="systemStore.installationDate" />
            </div>
          </v-col>
        </v-row>
      </div>

      <template v-if="isUpdateAvailable && !shouldSyncPtwEdgeUpdateStatus">
        <template v-if="available.update?.version">
          <VDivider class="my-6" />
          <h4>
            {{
              $t('AvailablePtw100SoftwareVersion', {
                version: available.update?.version,
              })
            }}
          </h4>

          <v-row>
            <v-col>
              <AvailableUpdateInfo
                :headline="$t('ANewUpdateIsAvailable')"
                :modelValue="available.update"
              />
              <Button
                variant="primary"
                :label="$t('RunUpdateNow')"
                :disabled="
                  loading ||
                  !permissionsStore.hasPermissions([
                    AppPermissionsEnum.GlobalSystemSettingsUpdateStart,
                  ])
                "
                @click="openUpdateWizard('update', true)"
              />
            </v-col>

            <v-col>
              <v-label>{{ $t('ReleaseNotes') }}</v-label>
              <p v-if="available.update.releaseNotesSummary" class="text-body-1 text-pre-line mb-5">
                {{ available.update.releaseNotesSummary.trim() }}
              </p>

              <ExternalLink
                v-if="available.update.releaseNotesUri"
                :text="$t('ReleaseNotes')"
                :link="available.update.releaseNotesUri"
              />
            </v-col>
          </v-row>
        </template>

        <template v-if="available.upgrade?.version">
          <h4 class="font-weight-bold mb-3">
            <VDivider class="my-6" />
            {{
              $t('AvailablePtw100SoftwareVersion', {
                version: available.upgrade?.version,
              })
            }}
          </h4>
          <v-row>
            <v-col>
              <AvailableUpdateInfo
                :headline="$t('ANewUpgradeIsAvailable')"
                :modelValue="available.upgrade"
              />

              <Button
                variant="primary"
                :label="$t('RunUpgradeNow')"
                :disabled="
                  loading ||
                  !permissionsStore.hasPermissions([
                    AppPermissionsEnum.GlobalSystemSettingsUpdateStart,
                  ])
                "
                @click="openUpdateWizard('upgrade', true)"
              />
            </v-col>

            <v-col>
              <v-label>{{ $t('ReleaseNotes') }}</v-label>
              <p
                v-if="available.upgrade.releaseNotesSummary"
                class="text-body-1 text-pre-line mb-5"
              >
                {{ available.upgrade.releaseNotesSummary.trim() }}
              </p>

              <ExternalLink
                v-if="available.upgrade.releaseNotesUri"
                :text="$t('ReleaseNotes')"
                :link="available.upgrade.releaseNotesUri"
              />
            </v-col>
          </v-row>
        </template>
      </template>

      <template
        v-if="
          checkedForUpdates &&
          !isUpdateAvailable &&
          installedVersion &&
          !checkForUpdatesErrorNotification
        "
      >
        <Notification :value="upToDateNotification" />
      </template>

      <template v-if="checkedForUpdates && checkForUpdatesErrorNotification">
        <Notification :value="checkForUpdatesErrorNotification" />
      </template>
    </v-sheet>

    <UpdateWizard
      v-if="available"
      v-model="isUpdateWizardOpen"
      :type="selectedUpdateType"
      :showStatus="showStatus"
      @update:modelValue="handleWizardState"
    />
  </BasePage>
</template>

<style scoped lang="scss">
:deep(.v-alert) {
  margin-top: 16px;
  .v-alert-title {
    margin-bottom: 0 !important;
  }
}
</style>
